
import React, { useState, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled,
  IconButton,
} from "@mui/material";
import { Check, Close as CloseIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { createNewAppelOffre } from "../../redux/appeloffreSlice";
import ConfirmationDialog from "../Common/ConfirmationQuitFormModal";

/* ────────────────────────────────────────────────────────────────────────── */
/*  🎨   DESIGN TOKENS – exactement ceux aperçus dans les mock‑ups           */
/* ────────────────────────────────────────────────────────────────────────── */
const NAVY       = "#2F4E8B";   // cercles, textes actifs/completed
const NAVY_LIGHT = "#4a6bad";   // hover primaires
const LINE_GREY  = "#C6CDD6";   // connecteurs du stepper
const LABEL_GREY = "#4F5565";   // libellés inactifs + textes secondaires
const OUTLINE    = "#D4D9E0";   // contours (bouton Back, champs focus)
const SUCCESS    = "#21D183";   // interrupteur actif

/* ───  TextField arrondi, transitions douces                                 */
const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "all .25s ease",
    "& fieldset": { borderColor: OUTLINE },
    "&:hover fieldset": { borderColor: NAVY_LIGHT },
    "&.Mui-focused fieldset": { borderColor: NAVY_LIGHT },
  },
  "& .MuiInputBase-input": { fontSize: 14 },
}));

/* ───  Fond dégradé identique aux captures                                   */
const GradientPaper = ({ children, sx }) => (
  <Paper
    elevation={8}
    sx={{
      borderRadius: 3,
      p: { xs: 3, md: 4 },
      width: { xs: "100%", md: 820 },
      mx: "auto",
      background:
        "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

/* ───  Connecteur personnalisé (trait fin gris)                              */
const AppelConnector = styled(StepConnector)(({ theme }) => ({
  top: 14,
  left: "calc(-50% + 16px)",
  right: "calc(50% + 16px)",
  "& .MuiStepConnector-line": {
    borderColor: LINE_GREY,
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

/* ───  Icône personnalisée (pastille + coche + numéro)                       */
const StepIconRoot = styled("div")(({ ownerState }) => ({
  zIndex: 1,
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: `2px solid ${NAVY}`,
  backgroundColor:
    ownerState.completed ? NAVY : ownerState.active ? NAVY : "#FFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: ownerState.completed || ownerState.active ? "#FFF" : NAVY,
  fontWeight: 700,
  fontSize: 13,
}));

function StepIconComponent(props) {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <Check fontSize="small" /> : icon}
    </StepIconRoot>
  );
}

/* ───  Étiquettes du stepper (French)                                         */
const steps = ["Informations générales", "Adresse", "Détails", "Commentaire"];

const AddAppelOffreForm = forwardRef(({ handleClose, onSuccess }, ref) => {
  const dispatch = useDispatch();
  const [loading, setLoading]       = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState("");
  const [autoSearch, setAutoSearch] = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  /* ───  Données du formulaire (inchangées)                                  */
  const [formData, setFormData] = useState({
    client_final: "",
    reference: "",
    reference_client: "",
    date_publication: "",
    date_cloture_offre: "",
    lieu_prestation: "",
    titre: "",
    descriptif: "",
    annee_experiences: "",
    tjm: "",
    commentaire: "",
    date_saisie: "",
    mode: "draft",
  });

  /* exposer dirty state au parent */
  useImperativeHandle(ref, () => ({
    isFormDirty: () => dirty,
    setIsFormDirty: setDirty,
  }));

  /* ──────────────────────────────────────────────────────────────────────── */
  /*   ☑ VALIDATION                                                          */
  /* ──────────────────────────────────────────────────────────────────────── */
  const stepIsValid = () => {
    switch (activeStep) {
      case 0:
        return (
          formData.date_publication &&
          formData.date_cloture_offre &&
          formData.titre.trim() &&
          formData.descriptif.trim()
        );
      case 1:
        return formData.lieu_prestation.trim();
      case 2:
        return formData.annee_experiences && formData.tjm;
      default:
        return true;
    }
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /*   🔄 HANDLERS                                                           */
  /* ──────────────────────────────────────────────────────────────────────── */
  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    setDirty(true);
  };

  const next = () => {
    if (!stepIsValid()) {
      setError(
        activeStep === 0
          ? "Veuillez compléter Date de publication, Date de clôture, Titre et Descriptif."
          : activeStep === 1
          ? "Veuillez renseigner l'adresse."
          : "Veuillez indiquer Année d'expérience et TJM."
      );
      return;
    }
    setError(null);
    activeStep === steps.length - 1 ? submit() : setActiveStep((s) => s + 1);
  };

  const back = () => setActiveStep((s) => s - 1);

  const cancel = () => (dirty ? setDialogOpen(true) : handleClose());

  const closeDialog = (confirm) => {
    setDialogOpen(false);
    if (confirm) handleClose();
  };

  const submit = async () => {
    setLoading(true);
    try {
      const data = {
        ...formData,
        reference: formData.reference || "interne",
        reference_client: formData.reference_client || "interne",
      };
      const res = await dispatch(createNewAppelOffre(data)).unwrap();
      onSuccess?.(res, autoSearch);
      setSuccess("Appel d'Offre ajouté avec succès !");
      setTimeout(() => setSuccess(""), 2500);
    } catch {
      setError("Erreur lors de l'ajout.");
    } finally {
      setLoading(false);
    }
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /*   📐 RENDER DES CHAMPS PAR ÉTAPE                                        */
  /* ──────────────────────────────────────────────────────────────────────── */
  const renderStep = (s) => {
    switch (s) {
      case 0:
        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
              gap: 2,
            }}
          >
            <CustomTextField
              label="Client Final"
              name="client_final"
              value={formData.client_final}
              onChange={onChange}
              fullWidth
            />
            <CustomTextField
              label="Référence"
              name="reference"
              value={formData.reference}
              onChange={onChange}
              fullWidth
            />
            <CustomTextField
              label="Référence Client"
              name="reference_client"
              value={formData.reference_client}
              onChange={onChange}
              fullWidth
            />
            <CustomTextField
              label="Date de Publication *"
              name="date_publication"
              type="date"
              value={formData.date_publication}
              onChange={onChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <CustomTextField
              label="Date de Clôture *"
              name="date_cloture_offre"
              type="date"
              value={formData.date_cloture_offre}
              onChange={onChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <CustomTextField
              label="Titre *"
              name="titre"
              value={formData.titre}
              onChange={onChange}
              fullWidth
            />
            <Box gridColumn="span 3">
              <CustomTextField
                label="Descriptif *"
                name="descriptif"
                value={formData.descriptif}
                onChange={onChange}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </Box>
        );
      case 1:
        return (
          <CustomTextField
            label="Adresse *"
            name="lieu_prestation"
            value={formData.lieu_prestation}
            onChange={onChange}
            fullWidth
          />
        );
      case 2:
        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
              gap: 2,
            }}
          >
            <CustomTextField
              label="Année Expérience *"
              name="annee_experiences"
              type="number"
              value={formData.annee_experiences}
              onChange={onChange}
              fullWidth
            />
            <CustomTextField
              label="TJM *"
              name="tjm"
              type="number"
              value={formData.tjm}
              onChange={onChange}
              fullWidth
            />
            <CustomTextField
              label="Mode"
              name="mode"
              value={formData.mode}
              onChange={onChange}
              select
              fullWidth
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="publié">Publié</MenuItem>
              <MenuItem value="interne">Interne</MenuItem>
            </CustomTextField>
          </Box>
        );
      case 3:
        return (
          <>
            <CustomTextField
              label="Commentaire"
              name="commentaire"
              value={formData.commentaire}
              onChange={onChange}
              multiline
              rows={4}
              fullWidth
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              label="Recherche automatique de candidats adaptés"
              sx={{ color: LABEL_GREY }}
              control={
                <Switch
                  checked={autoSearch}
                  onChange={(e) => setAutoSearch(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: SUCCESS },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: SUCCESS,
                    },
                  }}
                />
              }
            />
          </>
        );
      default:
        return null;
    }
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /*   🖥️  RENDER PRINCIPAL                                                 */
  /* ──────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <GradientPaper sx={{ position: "relative" }}>
        {/* Close Icon */}
        <IconButton
          onClick={cancel}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: LABEL_GREY,
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Titre du modal */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: NAVY,
            mb: 3,
            textAlign: "center",
          }}
        >
          Ajouter un appel d&apos;offre
        </Typography>

        {/* Stepper */}
<Stepper
  activeStep={activeStep}
  alternativeLabel
  connector={<AppelConnector />}
  sx={{ mb: 4 }}
>

          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={StepIconComponent}
                StepIconProps={{
                  icon: index < 9 ? `0${index + 1}` : index + 1,
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Feedback messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Contenu de l’étape */}
        <Box sx={{ mb: 4 }}>{renderStep(activeStep)}</Box>

        {/* Boutons de navigation */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {activeStep === 0 ? (
            <Box /> // Empty placeholder to maintain alignment
          ) : (
            <Button
              variant="outlined"
              onClick={back}
              sx={{
                borderColor: OUTLINE,
                color: LABEL_GREY,
                borderRadius: 1,
                textTransform: "none",
                px: 4,
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: OUTLINE,
                  color: NAVY,
                },
              }}
            >
              Précédent
            </Button>
          )}

          <Button
            variant="contained"
            onClick={next}
            disabled={loading || !stepIsValid()}
            sx={{
              backgroundColor: NAVY,
              borderRadius: 1,
              textTransform: "none",
              px: 5,
              fontWeight: "bold",
              "&:hover": { backgroundColor: NAVY_LIGHT },
              height: 42,
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#FFF" }} />
            ) : activeStep === steps.length - 1 ? (
              "Ajouter"
            ) : (
              "Suivant"
            )}
          </Button>
        </Box>
      </GradientPaper>

      {/* Dialogue de confirmation en cas de fermeture avec changements */}
      <ConfirmationDialog
        open={dialogOpen}
        title="Quitter le formulaire"
        message="Vous avez des modifications non enregistrées. Êtes‑vous sûr de vouloir quitter ?"
        onClose={closeDialog}
      />
    </>
  );
});

export default AddAppelOffreForm;
